import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Award, BarChart3, ChevronLeft, Clock, Info, Key, Loader2, Target, TrendingUp } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const PREC_CASE_TYPES = [
  { value: "personal_injury", label: "Personal Injury" },
  { value: "insurance_bad_faith", label: "Insurance Bad Faith" },
  { value: "employment", label: "Employment" },
  { value: "contract", label: "Contract" },
  { value: "medical_malpractice", label: "Medical Malpractice" },
  { value: "product_liability", label: "Product Liability" },
  { value: "fraud", label: "Fraud" },
  { value: "business_tort", label: "Business Tort" }
];


// ============ LITIGATION TREND ANALYZER ============

const TREND_JURISDICTIONS = [
  { value: "federal", label: "Federal Courts" },
  { value: "california", label: "California" },
  { value: "new_york", label: "New York" },
  { value: "texas", label: "Texas" },
  { value: "florida", label: "Florida" },
  { value: "illinois", label: "Illinois" },
  { value: "nationwide", label: "Nationwide" }
];

const TrendAnalyzerPage = () => {
  const [form, setForm] = useState({
    case_type: "personal_injury",
    jurisdiction: "federal",
    time_period_years: 5,
    specific_court: "",
    party_type: "both"
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  
  const analyze = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        case_type: form.case_type,
        jurisdiction: form.jurisdiction,
        time_period_years: form.time_period_years.toString(),
        specific_court: form.specific_court,
        party_type: form.party_type === 'both' ? '' : form.party_type
      });
      const res = await axios.post(`${API}/trends/analyze?${params}`);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/trends/history`);
      setHistory(res.data.analyses || []);
    } catch (e) {
      console.error(e);
    }
  };
  
  useEffect(() => {
    loadHistory();
  }, []);
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="trend-analyzer-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-6xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Litigation Trend Analyzer</h1>
            <Badge className="bg-gradient-to-r from-violet-600 to-purple-600">AI POWERED</Badge>
          </div>
          <p className="text-muted-foreground">Analyze litigation patterns, outcomes, and trends over time</p>
        </div>
        
        {/* Analysis Form */}
        <Card className="mb-6">
          <CardContent className="p-6 space-y-4">
            <div className="grid md:grid-cols-4 gap-4">
              <div className="space-y-1.5">
                <Label>Case Type</Label>
                <Select value={form.case_type} onValueChange={v => setForm({...form, case_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PREC_CASE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Jurisdiction</Label>
                <Select value={form.jurisdiction} onValueChange={v => setForm({...form, jurisdiction: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TREND_JURISDICTIONS.map(j => <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Time Period</Label>
                <Select value={form.time_period_years.toString()} onValueChange={v => setForm({...form, time_period_years: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">Last 3 years</SelectItem>
                    <SelectItem value="5">Last 5 years</SelectItem>
                    <SelectItem value="10">Last 10 years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Party Focus</Label>
                <Select value={form.party_type} onValueChange={v => setForm({...form, party_type: v})}>
                  <SelectTrigger><SelectValue placeholder="Both parties" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">Both parties</SelectItem>
                    <SelectItem value="plaintiff">Plaintiff trends</SelectItem>
                    <SelectItem value="defendant">Defendant trends</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-1.5">
              <Label>Specific Court (optional)</Label>
              <Input 
                value={form.specific_court}
                onChange={e => setForm({...form, specific_court: e.target.value})}
                placeholder="e.g., Northern District of California, LA Superior Court"
              />
            </div>
            
            <Button onClick={analyze} disabled={loading} data-testid="analyze-trends-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <TrendingUp className="h-4 w-4 mr-2" />}
              Analyze Trends
            </Button>
          </CardContent>
        </Card>
        
        {/* Results */}
        {result && (
          <div className="space-y-6 animate-fade-in" data-testid="trend-results">
            {/* Executive Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle>Trend Analysis Results</CardTitle>
                <CardDescription>{result.analysis_summary}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  {/* Outcome Stats */}
                  <div className="p-4 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
                    <p className="text-xs font-semibold text-blue-600 uppercase mb-1">Plaintiff Win Rate</p>
                    <p className="text-2xl font-bold text-blue-800">{result.outcome_statistics?.plaintiff_win_rate}%</p>
                  </div>
                  <div className="p-4 rounded-lg bg-gradient-to-br from-slate-50 to-gray-50 border border-slate-100">
                    <p className="text-xs font-semibold text-slate-600 uppercase mb-1">Defendant Win Rate</p>
                    <p className="text-2xl font-bold text-slate-800">{result.outcome_statistics?.defendant_win_rate}%</p>
                  </div>
                  <div className="p-4 rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100">
                    <p className="text-xs font-semibold text-emerald-600 uppercase mb-1">Settlement Rate</p>
                    <p className="text-2xl font-bold text-emerald-800">{result.outcome_statistics?.settlement_rate}%</p>
                  </div>
                  <div className="p-4 rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100">
                    <p className="text-xs font-semibold text-amber-600 uppercase mb-1">Median Award</p>
                    <p className="text-2xl font-bold text-amber-800">${(result.damages_trends?.median_award || 0).toLocaleString()}</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Filing Trends */}
                  <div className="p-4 rounded-lg border">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-blue-500" /> Filing Trends
                    </h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Direction:</span> {result.filing_trends?.overall_direction}</p>
                      <p><span className="font-medium">Annual Change:</span> {result.filing_trends?.annual_change_percent}%</p>
                      <p><span className="font-medium">Forecast:</span> {result.filing_trends?.forecast}</p>
                    </div>
                  </div>
                  
                  {/* Timing Analysis */}
                  <div className="p-4 rounded-lg border">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-purple-500" /> Timing Analysis
                    </h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Avg Resolution:</span> {result.timing_analysis?.average_time_to_resolution}</p>
                      <p><span className="font-medium">Settlement Timing:</span> {result.timing_analysis?.trial_vs_settlement_timing}</p>
                      <p><span className="font-medium">Backlog Impact:</span> {result.timing_analysis?.court_backlog_impact}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Key Trends */}
            {result.key_trends?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Key Trends</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {result.key_trends.map((trend, i) => (
                      <div key={i} className="p-4 rounded-lg bg-violet-50 border border-violet-100">
                        <h4 className="font-semibold text-violet-800 mb-1">{trend.trend_name}</h4>
                        <p className="text-sm mb-2">{trend.description}</p>
                        <p className="text-sm text-muted-foreground"><span className="font-medium">Impact:</span> {trend.impact}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Recommendations */}
            {result.recommendations?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="h-5 w-5 text-emerald-500" /> Strategic Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {result.recommendations.map((rec, i) => (
                      <div key={i} className="p-4 rounded-lg border">
                        <h4 className="font-semibold mb-1">{rec.recommendation}</h4>
                        <p className="text-sm text-muted-foreground mb-1">{rec.rationale}</p>
                        <Badge variant="outline" className="text-xs">{rec.timing}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Confidence & Limitations */}
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium">Analysis Confidence: {Math.round((result.confidence_level || 0.7) * 100)}%</span>
                  </div>
                  {result.data_limitations?.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Limitations: {result.data_limitations.join(', ')}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default TrendAnalyzerPage;
